import {ActivatedRoute, Router} from '@angular/router';
import {DtoDetail} from '../../../../../../../core/shared/dto/dto.detail';
import {MvsFormControlOverwrite} from '../../../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from '../../../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';
import {WidgetToolbarCallbackInterface} from '../../../../../../../core/shared/widget/widget-toolbar-callback.interface';

/**
 * A single foreign-key value to pre-fill (READ-only) on a create form opened from a
 * widget toolbar "+" button — `field` is the DTO attribute name (e.g. `caseTypeDtoId`,
 * `typeDtoId`, `caseTypeProductDtoId`, `stepBindingDtoId`), `value` its id.
 */
export interface CwFkPreset {
    field: string;
    value: number;
}

/**
 * Builds the `functionCallbacks` object for a WidgetFactory selectable/table widget so its
 * create ("+") button opens a create form with the given parent foreign key(s) pre-filled and
 * locked to READ access — the port of the old CwCaseTypeObjectBaseComponent.widgetToolbarCreate,
 * generalised to N foreign keys so two-FK junction children (e.g. a coverage-object or a
 * product-person-coverage) can preset every FK that is known from the current selection context.
 *
 * @param presets the parent foreign key(s) to preset READ-only on the create form
 */
export function cwWidgetToolbarCreate(presets: CwFkPreset[]): { onFunctionCreateNew: () => WidgetToolbarCallbackInterface } {
    return {
        onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
            const dto = new DtoDetail();
            const formControlOverwrite = new MvsFormControlOverwrite();
            for (const preset of presets) {
                dto[preset.field] = preset.value;
                formControlOverwrite.addField(preset.field, MvsFormFieldAccessEnum.READ);
            }
            return {
                dto: dto,
                formControlOverwrite: formControlOverwrite,
            };
        },
    };
}

/**
 * Reads the persisted active-tab key from the `?tab=` query param, restoring it only when it is
 * a valid tab of the current navigation point (otherwise the caller's default is kept). Uses the
 * route snapshot — safe to call from ngOnInit without a subscription.
 */
export function cwReadTabParam(route: ActivatedRoute, validTabs: string[], fallback: string): string {
    const tab = route.snapshot.queryParams['tab'];
    return tab && validTabs.includes(tab) ? tab : fallback;
}

/**
 * Persists the active tab into the `?tab=` query param (merge + replaceUrl so the active-nav
 * `?navItem=` is preserved and no history entry is pushed). Called from a user tab change, never
 * during change detection.
 */
export function cwPersistTabParam(router: Router, route: ActivatedRoute, tab: string): void {
    router.navigate([], {
        relativeTo: route,
        queryParams: {tab},
        queryParamsHandling: 'merge',
        replaceUrl: true
    });
}
